import { useCallback, useEffect, useRef, useState } from 'react';

import MessageBubble from 'components/_shared/MessageBubble';
import { makeStyles } from '@mui/styles';
import Input from '../../../../_shared/forms/Input.tsx';
import Label from "../../../../_shared/forms/Label";

const useStyles = makeStyles((theme) => ({
    questionBody: {
        marginBottom: '100px'
    },
    suggestionContainer: {
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '20px 30px',
        width: '75%',
        margin: 'auto',
        boxShadow: '0px 10px 80px rgba(0, 0, 0, 0.1)',
        position: 'relative'
    },
    suggestionTitle: {
        fontSize: '14px',
        fontWeight: '800',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        background: '#8882D8',
        borderRadius: '32px',
        padding: '5px 20px',
        color: '#FFFFFF',
        position: 'relative',
        top: '0.5em',
        left: '11%',
        zIndex: '2'
    },
    answerDiv: {
        width: "100%",
        padding: "1rem 0",
        gap: ".5rem",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column"
    },
    labelRadio: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        color: "#1F2152",
        whiteSpace: "nowrap",
        cursor: "pointer",
    },
    
    radio: {
        accentColor: "#7378E8",
        cursor: "pointer",
        transform: "scale(1.3)",
        transition: "transform 0.15s ease, filter 0.15s ease",
        outline: "none",
      
        "&:hover": {
            filter: "brightness(1.1)",
        },
      
        "&:focus-visible": {
            outline: "2px solid #7378E8",
            outlineOffset: "2px",
        },
      
        "&:checked": {
            transform: "scale(1.3)",
        },
    }
}));

const useDebounce = (callback, delay) => {
    const debounceRef = useRef(null);

    const debouncedCallback = useCallback(
        (...args) => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );

    return debouncedCallback;
};

const OPTIONS = [
    { text: "Strongly Agree", value: 5},
    { text: "Agree", value: 4},
    { text: "Neutral", value: 3},
    { text: "Disagree", value: 2},
    { text: "Strongly Disagree", value: 1},
];

const AgreementScaleQuestion = ({
    setAnswers,
    name,
    questions,
    questionLength,
    questionNum,
    answers
}) => {
    const classes = useStyles();
    const questionRef = useRef(null);

    const [isActive, setIsActive] = useState(false);
    const { question, id, quality_of_life_measure_id } = questions;

    const handleIntersectionDebounced = useDebounce(
        ([entry]) => {
            setIsActive(entry.isIntersecting);
        },
        200 // Adjust the debounce delay as needed
    );

    const currentAnswer =
        answers.find(a => a.touchpoint_question_id === id)?.answer || "";

    const handleChange = (value) => {
        setAnswers(prev => {
            const existingIndex = prev.findIndex(
                a => a.touchpoint_question_id === id
            );
    
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    answer: value
                };
                return updated;
            }
    
            return [...prev, { touchpoint_question_id: id, answer: value, quality_of_life_measure_id }];
        });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersectionDebounced, {
            threshold: 0.7
        });
        const questionActive = questionRef.current;

        if (questionActive) {
            observer.observe(questionActive);
        }

        return () => {
            if (questionActive) {
                observer.unobserve(questionActive);
            }
        };
    }, [handleIntersectionDebounced]);

    return (
        <div
            className={classes.questionBody}
            ref={questionRef}
            id={id}
            style={{ opacity: isActive ? 1 : 0.4, transition: 'opacity 0.4s' }}
        >
            <MessageBubble
                name={name}
                question={question}
                questionLength={questionLength}
                questionNum={questionNum}
            />
            <span className={classes.suggestionTitle}>
                Select an option
            </span>
            <div className={classes.suggestionContainer}>
                <div className={classes.answerDiv}>
                    {OPTIONS.map((option) => (
                        <Label
                            key={option.value}
                            className={classes.labelRadio}
                        >
                            <Input
                                type="radio"
                                name={option.text}
                                id={option.value}
                                value={option.value}
                                checked={String(currentAnswer) === String(option.value)}
                                onChange={() => handleChange(option.value)}
                                className={classes.radio}
                            />
                            {option.text}
                        </Label>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AgreementScaleQuestion;
